import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { apiClient } from '@/infrastructure/api/client'
import { resolveLanguage } from '@/application/i18n/language'
import type { Investigation, PaginatedInvestigations, HomepageFeed } from '@/domains/types'

export const investigationKeys = {
  // Locale-scoped for the same reason as investigationKeys.byId/read in
  // investigation.service.ts (TRANSLATION-PLAN.md §9 Phase 1) — these public
  // reads must react to a language switch too.
  paginated: (params: object, locale: string) =>
    ['investigations', 'paginated', params, locale] as const,
  recent: (limit: number, locale: string) => ['investigations', 'recent', limit, locale] as const,
  carousel: (locale: string) => ['investigations', 'carousel', locale] as const,
  homepage: (locale: string) => ['investigations', 'homepage', locale] as const,
}

/** Guarantees `investigations` is an array, whatever the payload looks like. */
function toInvestigations(page: Partial<PaginatedInvestigations> | undefined): Investigation[] {
  return Array.isArray(page?.investigations) ? page.investigations : []
}

export function useGetPaginatedInvestigations(params: {
  page?: number
  limit?: number
  search?: string
  category?: string
  sortBy?: string
  sortOrder?: string
  status?: string
}) {
  const { i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  return useQuery({
    queryKey: investigationKeys.paginated(params, locale),
    queryFn: async (): Promise<PaginatedInvestigations> => {
      const response = await apiClient.get<PaginatedInvestigations>('/investigations/paginated', {
        params: { ...params, lang: locale },
      })
      const body: Partial<PaginatedInvestigations> | undefined = response.data
      return {
        investigations: toInvestigations(body),
        total: body?.total ?? 0,
        page: body?.page ?? 1,
        limit: body?.limit ?? params.limit ?? 0,
      }
    },
    staleTime: 1000 * 60 * 2,
  })
}

export function useGetRecentInvestigations(limit = 5) {
  const { i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  return useQuery({
    queryKey: investigationKeys.recent(limit, locale),
    queryFn: async (): Promise<Investigation[]> => {
      const result = await apiClient.get<PaginatedInvestigations>('/investigations/paginated', {
        params: {
          page: 1,
          limit,
          sortBy: 'date',
          sortOrder: 'DESC',
          status: 'active',
          lang: locale,
        },
      })
      return toInvestigations(result.data)
    },
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * Returns investigations for the homepage carousel:
 * active ones first (up to `activeLimit`), followed by concluded ones
 * (inactive investigations with a non-null conclusion, up to `concludedLimit`).
 */
export function useGetCarouselInvestigations(activeLimit = 5, concludedLimit = 3) {
  const { i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  return useQuery({
    queryKey: investigationKeys.carousel(locale),
    queryFn: async (): Promise<Investigation[]> => {
      const [activeResult, inactiveResult] = await Promise.all([
        apiClient.get<PaginatedInvestigations>('/investigations/paginated', {
          params: {
            page: 1,
            limit: activeLimit,
            sortBy: 'date',
            sortOrder: 'DESC',
            status: 'active',
            lang: locale,
          },
        }),
        apiClient.get<PaginatedInvestigations>('/investigations/paginated', {
          params: {
            page: 1,
            limit: 20,
            sortBy: 'date',
            sortOrder: 'DESC',
            status: 'inactive',
            lang: locale,
          },
        }),
      ])
      const active = toInvestigations(activeResult.data)
      const concluded = toInvestigations(inactiveResult.data)
        .filter((investigation) => !!investigation.conclusion)
        .slice(0, concludedLimit)
      return [...active, ...concluded]
    },
    staleTime: 1000 * 60 * 5,
  })
}

export function useGetHomepageFeed() {
  const { i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  return useQuery({
    queryKey: investigationKeys.homepage(locale),
    queryFn: async (): Promise<HomepageFeed> => {
      const response = await apiClient.get<HomepageFeed>('/investigations/homepage', {
        params: { lang: locale },
      })
      const feed: Partial<HomepageFeed> | undefined = response.data
      return {
        lead: feed?.lead ?? null,
        headlines: Array.isArray(feed?.headlines) ? feed.headlines : [],
        latest: Array.isArray(feed?.latest) ? feed.latest : [],
      }
    },
    staleTime: 1000 * 60 * 5,
  })
}

export function useGetInvestigatorsCount() {
  return useQuery({
    queryKey: ['investigations', 'investigators-count'],
    queryFn: async (): Promise<number> => {
      const response = await apiClient.get<{ count: number }>('/investigations/investigators-count')
      return response.data.count
    },
    staleTime: 1000 * 60 * 5,
  })
}
